<template>
  <div class="desempenho-atendente-detalhe-page">
    <div class="dashboard-header mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="dashboard-header-titulo-row">
          <h2 class="dashboard-title">
            <router-link
              :to="rotaVoltar"
              class="dashboard-header-back mr-2"
              title="Voltar ao desempenho dos atendentes"
            >
              <feather-icon icon="ArrowLeftIcon" size="24" />
            </router-link>
            <feather-icon
              icon="UserIcon"
              size="32"
              class="titulo-icon-left"
            />
            <span class="titulo-texto">Desempenho: {{ nomeAtendente || 'Atendente' }}</span>
            <router-link
              :to="{ name: 'dashboard-main' }"
              class="dashboard-header-home ml-2"
              title="Ir para o início"
            >
              <feather-icon icon="HomeIcon" size="16" class="dashboard-header-home-icon" />
            </router-link>
          </h2>
        </div>
        <p class="text-muted mb-0 w-100 dashboard-header-subtitle">
          Gráficos por dia no período selecionado
        </p>
      </div>
    </div>

    <b-card v-if="textoFiltrosAplicados" class="mb-4 filtros-aplicados-card">
      <h6 class="filtros-aplicados-titulo mb-2">
        <feather-icon icon="FilterIcon" size="18" class="mr-1" />
        Filtros aplicados (herdados da página Desempenho dos Atendentes)
      </h6>
      <p class="text-muted small mb-0">{{ textoFiltrosAplicados }}</p>
    </b-card>

    <div
      v-if="carregando"
      class="text-center py-5"
    >
      <b-spinner type="grow" />
      <p class="mt-2 text-muted">Carregando dados...</p>
    </div>

    <template v-else>
      <div class="row">
        <div class="col-12 col-lg-6 mb-4">
          <b-card class="chart-card">
            <h5 class="card-title-custom mb-3">
              <feather-icon icon="MessageSquareIcon" size="20" class="mr-2" />
              Número de conversas por dia
            </h5>
            <vue-apex-chart
              type="bar"
              height="280"
              :options="opcoesBarra"
              :series="seriesConversasPorDia"
            />
          </b-card>
        </div>
        <div class="col-12 col-lg-6 mb-4">
          <b-card class="chart-card">
            <h5 class="card-title-custom mb-3">
              <feather-icon icon="SendIcon" size="20" class="mr-2" />
              Total de mensagens enviadas por dia
            </h5>
            <vue-apex-chart
              type="bar"
              height="280"
              :options="opcoesBarra"
              :series="seriesMensagensPorDia"
            />
          </b-card>
        </div>
        <div class="col-12 col-lg-6 mb-4">
          <b-card class="chart-card">
            <h5 class="card-title-custom mb-3">
              <feather-icon icon="ClockIcon" size="20" class="mr-2" />
              Tempo de primeira resposta por dia (média em min)
            </h5>
            <vue-apex-chart
              type="line"
              height="280"
              :options="opcoesLinha"
              :series="seriesPrimeiraRespostaPorDia"
            />
          </b-card>
        </div>
        <div class="col-12 col-lg-6 mb-4">
          <b-card class="chart-card">
            <h5 class="card-title-custom mb-3">
              <feather-icon icon="CheckCircleIcon" size="20" class="mr-2" />
              Tempo de resolução por dia (média em min)
            </h5>
            <vue-apex-chart
              type="line"
              height="280"
              :options="opcoesLinha"
              :series="seriesResolucaoPorDia"
            />
          </b-card>
        </div>
        <div class="col-12 mb-4">
          <b-card class="chart-card">
            <h5 class="card-title-custom mb-3">
              <feather-icon icon="AwardIcon" size="20" class="mr-2" />
              Contagem de resolução por dia
            </h5>
            <vue-apex-chart
              type="bar"
              height="280"
              :options="opcoesBarra"
              :series="seriesResolucaoContagemPorDia"
            />
          </b-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { buscaResolvidos, buscaMensagensPorAtendente, buscaChatsTeste } from '../../functions/basics/chat'

/**
 * Retorna os minutos entre inicioTimestamp e fimTimestamp que caem dentro do horario_funcionamento.
 */
function minutosDentroDoHorario(inicioTimestamp, fimTimestamp, horarioFuncionamento) {
  if (!inicioTimestamp || !fimTimestamp || !horarioFuncionamento || !horarioFuncionamento.length) return null
  const inicio = new Date(inicioTimestamp)
  const fim = new Date(fimTimestamp)
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime()) || inicio >= fim) return 0
  let totalMin = 0
  const dayStart = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate(), 0, 0, 0, 0)
  const dayEnd = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate(), 23, 59, 59, 999)
  let d = new Date(dayStart)
  while (d <= dayEnd) {
    const dia = d.getDay()
    const year = d.getFullYear()
    const month = d.getMonth()
    const date = d.getDate()
    const intervalos = horarioFuncionamento.filter(h => h.dia === dia)
    for (let i = 0; i < intervalos.length; i++) {
      const int = intervalos[i]
      const [abreH, abreM] = (int.abre || '00:00').split(':').map(Number)
      const [fechaH, fechaM] = (int.fecha || '23:59').split(':').map(Number)
      const intervalStart = new Date(year, month, date, abreH || 0, abreM || 0, 0, 0)
      const intervalEnd = new Date(year, month, date, fechaH || 23, fechaM || 59, 59, 999)
      const overlapStart = new Date(Math.max(inicio.getTime(), intervalStart.getTime()))
      const overlapEnd = new Date(Math.min(fim.getTime(), intervalEnd.getTime()))
      if (overlapStart < overlapEnd) {
        totalMin += (overlapEnd.getTime() - overlapStart.getTime()) / 60000
      }
    }
    d.setDate(d.getDate() + 1)
  }
  return totalMin
}

function refPrimeiraResposta(at) {
  const usoInicio = at.atendimento_bot === false && (at.fim_bot == null || at.fim_bot === undefined)
  if (usoInicio && at.inicio) return new Date(at.inicio).getTime()
  if (at.fim_bot) return new Date(at.fim_bot).getTime()
  return null
}

function calcularTempoPrimeiraRespostaMin(at) {
  const primeiraRespostaEm = at.primeira_mensagem_atendente_em ? new Date(at.primeira_mensagem_atendente_em).getTime() : null
  if (primeiraRespostaEm == null || Number.isNaN(primeiraRespostaEm)) return null
  const usoInicio = at.atendimento_bot === false && (at.fim_bot == null || at.fim_bot === undefined)
  const ref = usoInicio
    ? (at.inicio ? new Date(at.inicio).getTime() : null)
    : (at.fim_bot ? new Date(at.fim_bot).getTime() : null)
  if (ref == null || Number.isNaN(ref)) return null
  return (primeiraRespostaEm - ref) / 1000 / 60
}

function dateToKey(d) {
  const x = new Date(d)
  const y = x.getFullYear()
  const m = String(x.getMonth() + 1).padStart(2, '0')
  const day = String(x.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'DesempenhoAtendenteDetalhe',
  components: { 'vue-apex-chart': VueApexCharts },
  data() {
    return {
      carregando: true,
      resolvidosFiltrados: [],
      diasLabels: [],
      dadosPorDia: {},
      horarioFuncionamentoExibicao: [],
      listaChats: [],
      opcoesBarraBase: {
        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
        dataLabels: { enabled: true },
        xaxis: { type: 'category', labels: { rotate: -45 } },
        yaxis: { labels: { formatter: v => (Number.isInteger(v) ? v : v.toFixed(1)) } },
        colors: ['#7367f0'],
      },
      opcoesLinhaBase: {
        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
        stroke: { curve: 'smooth', width: 2 },
        dataLabels: { enabled: true },
        xaxis: { type: 'category', labels: { rotate: -45 } },
        yaxis: { labels: { formatter: v => (Number.isInteger(v) ? v : v.toFixed(1)) } },
        colors: ['#28c76f'],
      },
    }
  },
  computed: {
    uuid() {
      return this.$route.query.uuid || null
    },
    atendenteId() {
      const id = this.$route.query.atendente_id
      if (id === 'bot') return 'bot'
      if (id != null && id !== '') return String(id)
      return null
    },
    nomeAtendente() {
      return this.$route.query.nome || 'Atendente'
    },
    periodoInicio() {
      return this.$route.query.periodo_inicio || null
    },
    periodoFim() {
      return this.$route.query.periodo_fim || null
    },
    considerarHorario() {
      return this.$route.query.considerar_horario === '1' || this.$route.query.considerar_horario === 'true'
    },
    rotaVoltar() {
      const q = {}
      if (this.uuid) q.uuid = this.uuid
      if (this.periodoInicio) q.periodo_inicio = this.periodoInicio
      if (this.periodoFim) q.periodo_fim = this.periodoFim
      if (this.considerarHorario) q.considerar_horario = '1'
      return { name: 'chat-desempenho-atendentes', query: q }
    },
    periodoFormatado() {
      const i = this.periodoInicio
      const f = this.periodoFim
      if (!i || !f) return ''
      const fmt = d => new Date(d).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${fmt(i)} – ${fmt(f)}`
    },
    textoFiltrosAplicados() {
      const partes = []
      const i = this.periodoInicio ? new Date(this.periodoInicio) : null
      const f = this.periodoFim ? new Date(this.periodoFim) : null
      if (i && f && !Number.isNaN(i.getTime()) && !Number.isNaN(f.getTime())) {
        const fmt = d => d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })
        partes.push(`Período: ${fmt(i)} a ${fmt(f)}`)
      }
      const chat = this.listaChats.find(c => c.chat_token === this.uuid)
      if (chat && (chat.name || chat.chat_token)) {
        partes.push(`central: ${chat.name || chat.chat_token}`)
      }
      partes.push(`Considerar somente horário de atendimento: ${this.considerarHorario ? 'Sim' : 'Não'}`)
      if (this.considerarHorario && (this.horarioFuncionamentoExibicao || []).length) {
        const h = this.horarioFuncionamentoExibicao
        const nomesDia = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
        const porDia = {}
        h.forEach(({ dia, abre, fecha }) => {
          if (!porDia[dia]) porDia[dia] = []
          porDia[dia].push(`${abre || ''}–${fecha || ''}`)
        })
        const txt = Object.keys(porDia)
          .sort((a, b) => Number(a) - Number(b))
          .map(d => `${nomesDia[Number(d)]}: ${porDia[d].join(', ')}`)
          .join(' | ')
        partes.push(`Horários: ${txt}`)
      }
      return partes.length ? partes.join(' · ') : ''
    },
    textoHorarioConfigurado() {
      const h = this.horarioFuncionamentoExibicao || []
      if (!h.length) return ''
      const nomesDia = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
      const porDia = {}
      h.forEach(({ dia, abre, fecha }) => {
        if (!porDia[dia]) porDia[dia] = []
        porDia[dia].push(`${abre || ''}-${fecha || ''}`)
      })
      return Object.keys(porDia)
        .sort((a, b) => Number(a) - Number(b))
        .map(d => `${nomesDia[Number(d)]}: ${porDia[d].join(', ')}`)
        .join(' | ')
    },
    opcoesBarra() {
      const labels = this.diasLabels.map(d => {
        const [y, m, day] = d.split('-')
        return `${day}/${m}`
      })
      return {
        ...this.opcoesBarraBase,
        xaxis: { ...this.opcoesBarraBase.xaxis, categories: labels },
      }
    },
    opcoesLinha() {
      const labels = this.diasLabels.map(d => {
        const [y, m, day] = d.split('-')
        return `${day}/${m}`
      })
      const yaxisBase = this.opcoesLinhaBase.yaxis || {}
      return {
        ...this.opcoesLinhaBase,
        xaxis: { ...this.opcoesLinhaBase.xaxis, categories: labels },
        yaxis: {
          ...yaxisBase,
          title: { text: 'Média (min)' },
          labels: {
            ...(yaxisBase.labels || {}),
            formatter: v => (v != null && v !== '' ? `${Number(v)} min` : ''),
          },
        },
        dataLabels: {
          ...(this.opcoesLinhaBase.dataLabels || {}),
          formatter: v => (v != null && v !== '' ? `${Number(v)} min` : ''),
        },
        tooltip: {
          y: { formatter: v => (v != null && v !== '' ? `${Number(v)} min` : '') },
        },
      }
    },
    temDadosMensagens() {
      return this.diasLabels.some(d => (this.dadosPorDia[d] && this.dadosPorDia[d].mensagens) > 0)
    },
    seriesConversasPorDia() {
      return [{
        name: 'Conversas',
        data: this.diasLabels.map(d => this.dadosPorDia[d]?.conversas ?? 0),
      }]
    },
    seriesMensagensPorDia() {
      return [{
        name: 'Mensagens',
        data: this.diasLabels.map(d => this.dadosPorDia[d]?.mensagens ?? 0),
      }]
    },
    seriesPrimeiraRespostaPorDia() {
      return [{
        name: 'Tempo 1ª resposta (min)',
        data: this.diasLabels.map(d => {
          const v = this.dadosPorDia[d]?.media_primeira_resposta
          return v != null ? Math.round(v * 10) / 10 : 0
        }),
      }]
    },
    seriesResolucaoPorDia() {
      return [{
        name: 'Tempo resolução (min)',
        data: this.diasLabels.map(d => {
          const v = this.dadosPorDia[d]?.media_resolucao
          return v != null ? Math.round(v * 10) / 10 : 0
        }),
      }]
    },
    seriesResolucaoContagemPorDia() {
      return [{
        name: 'Resoluções',
        data: this.diasLabels.map(d => this.dadosPorDia[d]?.resolucoes ?? 0),
      }]
    },
  },
  watch: {
    '$route.query': {
      handler() {
        this.carregarDados()
      },
      deep: true,
    },
  },
  mounted() {
    buscaChatsTeste().then(res => {
      this.listaChats = res?.chats || []
    })
    this.carregarDados()
  },
  methods: {
    async carregarDados() {
      const uuid = this.uuid
      const inicio = this.periodoInicio
      const fim = this.periodoFim
      if (!uuid || !inicio || !fim) {
        this.carregando = false
        this.diasLabels = []
        this.dadosPorDia = {}
        return
      }

      this.carregando = true
      try {
        let horarioFuncionamento = []
        if (this.considerarHorario) {
          const contas = await buscaChatsTeste()
          const chat = (contas?.chats || []).find(c => c.chat_token === uuid)
          horarioFuncionamento = chat?.horario_funcionamento || []
          this.horarioFuncionamentoExibicao = horarioFuncionamento
        } else {
          this.horarioFuncionamentoExibicao = []
        }

        const dados = await buscaResolvidos(uuid, inicio, fim, 'all')
        const resolvidos = dados?.resolvidos || []

        const idAlvo = this.atendenteId
        const filtrado = resolvidos.filter(at => {
          if (idAlvo === 'bot') return at.atendimento_bot === true
          return !at.atendimento_bot && (at.atendente_ativo?.id == idAlvo || String(at.atendente_ativo?.id) === idAlvo)
        })
        this.resolvidosFiltrados = filtrado

        const inicioDate = new Date(inicio)
        const fimDate = new Date(fim)
        const diasLabels = []
        const d = new Date(inicioDate)
        d.setHours(0, 0, 0, 0)
        const fimD = new Date(fimDate)
        fimD.setHours(23, 59, 59, 999)
        while (d <= fimD) {
          diasLabels.push(dateToKey(d))
          d.setDate(d.getDate() + 1)
        }
        this.diasLabels = diasLabels

        const dadosPorDia = {}
        diasLabels.forEach(dia => {
          dadosPorDia[dia] = {
            conversas: 0,
            mensagens: 0,
            soma_primeira_resposta: 0,
            count_primeira_resposta: 0,
            soma_resolucao: 0,
            count_resolucao: 0,
            resolucoes: 0,
          }
        })

        const considerarHorarioCalc = this.considerarHorario && horarioFuncionamento.length > 0
        filtrado.forEach(at => {
          const diaInicio = dateToKey(at.inicio)
          const diaFim = at.fim || at.data_resolucao || at.ultima_mensagem_em
          const diaResolucao = diaFim ? dateToKey(diaFim) : diaInicio

          if (dadosPorDia[diaInicio] != null) {
            dadosPorDia[diaInicio].conversas += 1
            let primeiraMin = null
            if (at.primeira_mensagem_atendente_em) {
              const ref = refPrimeiraResposta(at)
              if (ref != null) {
                primeiraMin = considerarHorarioCalc
                  ? minutosDentroDoHorario(ref, new Date(at.primeira_mensagem_atendente_em).getTime(), horarioFuncionamento)
                  : calcularTempoPrimeiraRespostaMin(at)
              }
            }
            if (primeiraMin != null && !Number.isNaN(primeiraMin)) {
              dadosPorDia[diaInicio].soma_primeira_resposta += primeiraMin
              dadosPorDia[diaInicio].count_primeira_resposta += 1
            }
            const inicioAt = at.inicio ? new Date(at.inicio).getTime() : null
            const fimAt = diaFim ? new Date(diaFim).getTime() : null
            let tempoResolucaoMin = null
            if (inicioAt && fimAt) {
              tempoResolucaoMin = considerarHorarioCalc
                ? minutosDentroDoHorario(inicioAt, fimAt, horarioFuncionamento)
                : (fimAt - inicioAt) / 1000 / 60
            }
            if (tempoResolucaoMin != null && !Number.isNaN(tempoResolucaoMin)) {
              dadosPorDia[diaInicio].soma_resolucao += tempoResolucaoMin
              dadosPorDia[diaInicio].count_resolucao += 1
            }
          }
          if (dadosPorDia[diaResolucao] != null && diaResolucao) {
            dadosPorDia[diaResolucao].resolucoes += 1
          }
        })

        // Mensagens por dia: requisição ao endpoint /api/report/messages-by-attendant (apenas para atendente humano)
        // Resposta: { success, uuid, mensagens: [ { timestamp: "2026-02-28T20:01:01.195Z", ... }, ... ] }
        if (idAlvo && idAlvo !== 'bot') {
          try {
            const msgData = await buscaMensagensPorAtendente(uuid, idAlvo, inicio, fim)
            const lista = msgData?.mensagens || []
            lista.forEach(msg => {
              const ts = msg?.timestamp
              if (!ts) return
              const dia = dateToKey(ts)
              if (dadosPorDia[dia] != null) {
                dadosPorDia[dia].mensagens = (dadosPorDia[dia].mensagens || 0) + 1
              }
            })
          } catch (errMsg) {
            console.warn('Erro ao carregar mensagens por atendente:', errMsg)
          }
        }

        diasLabels.forEach(dia => {
          const m = dadosPorDia[dia]
          if (m.count_primeira_resposta) {
            m.media_primeira_resposta = m.soma_primeira_resposta / m.count_primeira_resposta
          } else {
            m.media_primeira_resposta = null
          }
          if (m.count_resolucao) {
            m.media_resolucao = m.soma_resolucao / m.count_resolucao
          } else {
            m.media_resolucao = null
          }
        })

        this.dadosPorDia = dadosPorDia
      } catch (err) {
        console.error('Erro ao carregar detalhe do atendente:', err)
        this.$bvToast.toast(err.message || 'Erro ao carregar dados.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.diasLabels = []
        this.dadosPorDia = {}
      } finally {
        this.carregando = false
      }
    },
  },
}
</script>

<style scoped>
.desempenho-atendente-detalhe-page {
  padding: 1rem 1.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.dashboard-header {
  padding: 1rem 0;
}

.dashboard-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.dashboard-header-back {
  color: var(--primary);
  display: inline-flex;
  align-items: center;
}

.dashboard-header-back:hover {
  color: var(--primary);
  opacity: 0.85;
}

.titulo-icon-left {
  margin-right: 0.5rem;
}

.dashboard-header-home {
  display: inline-flex;
  align-items: center;
  color: var(--primary);
}

.dashboard-header-home:hover {
  color: var(--primary);
  opacity: 0.85;
}

.filtros-aplicados-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border-left: 4px solid #9A3092;
}

.filtros-aplicados-titulo {
  font-weight: 600;
  color: #5e5873;
  font-size: 0.95rem;
}

.chart-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-title-custom {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}
</style>

<style>
/* Fazer o conteúdo ocupar toda a largura da página (sem espaço vazio à direita) */
body:has(.desempenho-atendente-detalhe-page) .content-area-wrapper {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
  width: 100% !important;
}

body:has(.desempenho-atendente-detalhe-page) .app-content.content {
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-width: 100% !important;
}

body:has(.desempenho-atendente-detalhe-page) .content-wrapper,
body:has(.desempenho-atendente-detalhe-page) .content-body {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

body:has(.desempenho-atendente-detalhe-page) .content-right {
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;
}
</style>
